import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom";
import useErroPadrao from '../../../hooks/useErroPadrao';
import useHelp from '../../../hooks/useHelp';
import { CHAVE_TOKEN, real, dataBR } from "../../../utils/formata";

// Quem assina pela Help em todo contrato
const HELP = {
    nome: "HELP EMPRESARIAL",
    cnpj: "46.643.823/0001-36",
    responsavel: "Daniele Caroline Dias de Morais",
    cidade: "Itajaí/SC",
};

// O caixa fecha neste horário (mesmo valor do config.json do robô)
const HORA_FECHAMENTO = "17:00";

// O contrato editado à mão precisa virar PDF com a MESMA cara que tem na tela. O corpo carrega
// estilo inline em quase tudo; o que vem de fora são estas classes e o tamanho do papel.
const CSS_PDF = `
  @page { size: A4; margin: 16mm 18mm; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #fff; color: #111; font-family: "Segoe UI", Arial, sans-serif; font-size: 11pt; line-height: 1.55; }
  .folha-contrato { padding: 0; background: #fff; color: #111; }
  .folha-contrato + .folha-contrato { page-break-before: always; }
  .tabela-contrato { width: 100%; table-layout: fixed; border-collapse: collapse; margin: 1.5em 0; font-size: 0.85em; color: #111; }
  .tabela-contrato th, .tabela-contrato td { border: 1px solid #ccc; text-align: center; overflow-wrap: break-word; word-break: break-word; }
  .tabela-contrato th { padding: 8px 4px; }
  .tabela-contrato td { padding: 6px 4px; }
  .assinaturas-contrato { display: flex; justify-content: space-around; margin-top: 3em; gap: 2em; }
  .he-card { box-shadow: none; border: 0; }
`;
const montarHtmlParaPdf = (fragmento, titulo) =>
    `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"><title>${titulo}</title>` +
    `<style>${CSS_PDF}</style></head><body>${fragmento}</body></html>`;

// Valor por extenso pra nota promissória ("três mil e seiscentos reais")
const UN = ["", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove", "dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"];
const DEZ = ["", "", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"];
const CEN = ["", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"];
function ate999(n) {
    if (n === 0) return "";
    if (n === 100) return "cem";
    const c = Math.floor(n / 100), r = n % 100;
    const partes = [];
    if (c) partes.push(CEN[c]);
    if (r < 20) { if (r) partes.push(UN[r]); }
    else { const d = Math.floor(r / 10), u = r % 10; partes.push(u ? `${DEZ[d]} e ${UN[u]}` : DEZ[d]); }
    return partes.join(" e ");
}
// CNPJ/CPF salvos só com dígitos saem formatados no contrato (67813910000164 → 67.813.910/0001-64)
function docFmt(v) {
    const d = String(v || "").replace(/\D/g, "");
    if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
    return String(v || "");
}
function porExtenso(valor) {
    const n = Math.round(Number(valor || 0) * 100);
    const reais = Math.floor(n / 100), cent = n % 100;
    const mil = Math.floor(reais / 1000), resto = reais % 1000;
    let t = mil
        ? (mil === 1 ? "mil" : `${ate999(mil)} mil`) + (resto ? (resto < 100 || resto % 100 === 0 ? " e " : " ") + ate999(resto) : "")
        : ate999(resto);
    t = t ? `${t} ${reais === 1 ? "real" : "reais"}` : "zero real";
    if (cent) t += ` e ${ate999(cent)} ${cent === 1 ? "centavo" : "centavos"}`;
    return t;
}

// Instrumento de contrato pronto pra imprimir e assinar — com confissão de dívida, penhor
// mercantil (garantia sobre bens do estabelecimento), título executivo e nota promissória.
export default function ContratoImpressao() {
    const [emp, setEmp] = useState(null);
    const [editando, setEditando] = useState(false);
    const [htmlSalvo, setHtmlSalvo] = useState(null); // texto editado pela Dani, guardado neste navegador
    const corpoRef = useRef(null);
    const [numeroRobo, setNumeroRobo] = useState("");
    const [envio, setEnvio] = useState({ estado: "", msg: "" });
    const { consultarEmprestimo, statusRobo } = useHelp();
    const { setErroPadrao } = useErroPadrao();
    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        if (!localStorage.getItem(CHAVE_TOKEN)) {
            navigate("/login");
            return;
        }
        consultarEmprestimo(id)
            .then((data) => setEmp(data))
            .catch((error) => setErroPadrao(error));
    }, [id, consultarEmprestimo, setErroPadrao, navigate]);

    useEffect(() => {
        try { setHtmlSalvo(localStorage.getItem(`contrato-editado-${id}`) || null); } catch { setHtmlSalvo(null); }
    }, [id]);

    // número do robô: o botão "Enviar pelo WhatsApp" abre o chat dele com o comando pronto
    useEffect(() => {
        statusRobo().then((r) => setNumeroRobo(String(r?.numero || "").replace(/\D/g, ""))).catch(() => {});
    }, [statusRobo]);

    if (!emp) {
        return <div style={{padding: "3em", width: "100%", textAlign: "center"}}><span className="spinner-border" style={{color: "#d4af37"}}></span></div>;
    }

    // Campo sem dado não sai "sob nº ," nem "representada por A CONFIRMAR": vira linha pra preencher à mão (contrato #146, 17/09)
    const LINHA_EM_BRANCO = "______________________________";
    const empresaBruta = emp.Empresa || {};
    const empresa = {
        ...empresaBruta,
        cnpj: String(empresaBruta.cnpj || "").trim() && !/^n[ãa]o informado$/i.test(String(empresaBruta.cnpj).trim()) ? empresaBruta.cnpj : "",
        responsavel: String(empresaBruta.responsavel || "").trim() && !/^a confirmar\b/i.test(String(empresaBruta.responsavel).trim()) ? empresaBruta.responsavel : LINHA_EM_BRANCO,
        // nome fantasia igual à razão social não se repete: "Restaurante Lá Fe (Restaurante Lá Fe)" (Dani, 17/09)
        nomeFantasia: String(empresaBruta.nomeFantasia || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase() === String(empresaBruta.razaoSocial || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase() ? "" : empresaBruta.nomeFantasia,
    };
    const parcelas = [...(emp.Parcelas || [])].sort((a, b) => a.numero - b.numero);
    const ultimoVencimento = parcelas.length ? parcelas[parcelas.length - 1].dataVencimento : emp.dataPrimeiroVencimento;
    const numero = String(emp.id).padStart(5, "0");
    const diarias = String(emp.periodicidade || "").startsWith("diaria");
    const parcelaNome = diarias ? "diária" : "parcela";
    // endereço da visita que é só o pino do mapa ("-26.39551,-48.60252 (https://maps…)") não serve pra contrato: vale o do cadastro (17/09)
    const soPinoDoMapa = (t) => /-?\d{1,2}\.\d{4,}\s*,\s*-?\d{1,3}\.\d{4,}|maps\.google|google\.[a-z.]+\/maps/i.test(String(t || ""));
    const enderecoEstab = (emp.visitaEndereco && !soPinoDoMapa(emp.visitaEndereco) ? emp.visitaEndereco : "") || (empresa.endereco && !soPinoDoMapa(empresa.endereco) ? empresa.endereco : "") || emp.visitaEndereco || empresa.endereco || "";
    // Bens em penhor ficam cadastrados como documento do contrato, com o nome
    // "BEM EM PENHOR: descrição | marca/modelo/série | estado | avaliação" — e saem prontos no Anexo I
    const bensPenhor = (emp.Documentos || [])
        .filter((d) => /^BEM EM PENHOR/i.test(d.nome || ""))
        .map((d) => {
            const p = String(d.nome).replace(/^BEM EM PENHOR:?\s*/i, "").split("|").map((s) => s.trim());
            return { descricao: p[0] || "", detalhes: p[1] || "", estado: p[2] || "", avaliacao: p[3] || "" };
        });
    // contrato assinado online: com bem cadastrado não sobra linha vazia pra preencher (Dani, 17/09)
    const linhasVaziasAnexo = bensPenhor.length ? 0 : 3;
    const celulaDoBem = (v) => { const s = String(v || "").trim(); return !s || /^(n[ãa]o informad|a conferir|conferir|marca \/ modelo \/ n[ºo°] de s[ée]rie: conferir|marca \/ modelo \/ n[ºo°] de s[ée]rie$)/i.test(s) ? "—" : s; }; // célula sem dado de verdade vira "—" (17/09)
    // contrato é assinado online: sem CNPJ vale o CPF do responsável; sem nenhum, a frase sai (Dani, 17/09)
    const docCliente = empresa.cnpj || empresa.cpfResponsavel || "";
    const valorDaAvaliacao = (t) => { const s = String(t || ""); if (!s.trim() || /mais de|menos de|a informar|a conferir|_{2,}/i.test(s)) return null; const m = s.match(/(\d{1,3}(?:\.\d{3})+|\d+)(?:,(\d{2}))?/); return m ? Number(m[1].replace(/\./g, "")) + (m[2] ? Number(m[2]) / 100 : 0) : null; };
    const avaliacoes = bensPenhor.map((b) => valorDaAvaliacao(b.avaliacao));
    const totalAvaliacao = bensPenhor.length && avaliacoes.every((v) => v !== null) ? `R$ ${avaliacoes.reduce((s, v) => s + v, 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "R$";
    // Chave PIX da liberação também fica como registro do contrato ("CHAVE PIX: …") e entra na Cláusula 1ª
    const chavePix = ((emp.Documentos || []).map((d) => d.nome || "").find((n) => /^CHAVE PIX/i.test(n)) || "").replace(/^CHAVE PIX:?\s*/i, "").trim();
    // Contrato assinado online: antes de enviar, a página diz o que ainda está sem dado (Dani, 17/09)
    const faltamNoContrato = [
        !String(empresaBruta.responsavel || "").trim() || /^a confirmar\b/i.test(String(empresaBruta.responsavel || "").trim()) ? "nome do responsável (vem do documento com foto)" : "",
        !docCliente ? "CPF do responsável ou CNPJ" : "",
        !enderecoEstab || soPinoDoMapa(enderecoEstab) || /_{2,}/.test(enderecoEstab) ? "endereço do estabelecimento" : "",
        !bensPenhor.length ? "bem em penhor (Anexo I)" : "",
        bensPenhor.some((b) => valorDaAvaliacao(b.avaliacao) === null) ? "valor aproximado de cada bem" : "",
    ].filter(Boolean);

    // "Sempre colocar todos os dados da cliente no contrato" (Dani, 14/09). RG, nascimento e nome
    // da mãe não têm coluna própria: o robô grava nas observações ao ler o documento.
    // Mesmo texto do robô — robo-whatsapp/contratoHtml.js. Mudou lá, muda aqui.
    const obsTexto = String(empresa.obs || "");
    const doDoc = (re) => (obsTexto.match(re)?.[1] || "").trim().replace(/[.;·]+$/, "").trim();
    const qualificacaoResp = [
        empresa.cpfResponsavel ? `CPF nº ${docFmt(empresa.cpfResponsavel)}` : "",
        doDoc(/\bRG\s+([0-9A-Za-z.\-/ ]{4,20})/i) ? `RG nº ${doDoc(/\bRG\s+([0-9A-Za-z.\-/ ]{4,20})/i)}` : "",
        doDoc(/\bnascimento\s+(\d{2}\/\d{2}\/\d{4})/i) ? `nascido(a) em ${doDoc(/\bnascimento\s+(\d{2}\/\d{2}\/\d{4})/i)}` : "",
        doDoc(/\bm[ãa]e\s+([A-Za-zÀ-ÿ'´` .]{5,80})/i) ? `filho(a) de ${doDoc(/\bm[ãa]e\s+([A-Za-zÀ-ÿ'´` .]{5,80})/i)}` : "",
        empresa.telefone ? `telefone ${empresa.telefone}` : "",
        empresa.email ? `e-mail ${empresa.email}` : "",
    ].filter(Boolean).join(", ");

    // Edição livre antes de imprimir: o que a Dani digitar fica salvo por contrato neste navegador
    const chaveEdicao = `contrato-editado-${emp.id}`;
    // Antes de liberar a edição, o HTML vira "texto morto" (dangerouslySetInnerHTML): o React para de
    // reconciliar esse pedaço e a edição livre não derruba a página na hora de concluir.
    const iniciarEdicao = () => {
        if (!htmlSalvo && corpoRef.current) setHtmlSalvo(corpoRef.current.innerHTML);
        setEditando(true);
    };
    const concluirEdicao = () => {
        if (corpoRef.current) {
            const html = corpoRef.current.innerHTML;
            try { localStorage.setItem(chaveEdicao, html); } catch {}
            setHtmlSalvo(html);
        }
        setEditando(false);
    };
    const restaurarOriginal = () => {
        try { localStorage.removeItem(chaveEdicao); } catch {}
        setHtmlSalvo(null);
        setEditando(false);
        // o robô guarda a versão editada em disco — avisa que ela não vale mais
        fetch(`http://127.0.0.1:3456/contrato/${emp.id}?descartar=1`, {
            method: "POST",
            headers: { "X-Help": "1", "Content-Type": "application/json" },
            body: JSON.stringify({ editado: null }),
        }).catch(() => {});
    };
    const imprimir = () => {
        if (editando) concluirEdicao();
        setTimeout(() => window.print(), 150);
    };

    // Enviar pelo WhatsApp com UM clique: o robô roda no PC da Dani e atende em 127.0.0.1:3456.
    // No celular (ou se o robô não estiver neste PC) cai no WhatsApp com o comando pronto.
    const abrirWhatsAppComComando = () => {
        const texto = encodeURIComponent(`#contrato ${emp.id}`);
        const numero = numeroRobo || "554789163477";
        const celular = /Android|iPhone|iPad/i.test(navigator.userAgent);
        window.open(celular ? `https://wa.me/${numero}?text=${texto}` : `https://web.whatsapp.com/send?phone=${numero}&text=${texto}`, "_blank", "noopener");
        setEnvio({ estado: "manual", msg: "Abri o WhatsApp com o comando pronto — toque em ENVIAR lá que o robô manda o PDF." });
    };
    const enviarPeloRobo = async (soParaMim) => {
        if (editando) concluirEdicao();
        setEnvio({ estado: "enviando", msg: soParaMim ? "Gerando o PDF pra você conferir…" : "Gerando o PDF e enviando pro cliente…" });
        const controle = new AbortController();
        const alarme = setTimeout(() => controle.abort(), 90000);
        try {
            // A edição vive no navegador (localStorage). Sem mandar junto, o robô gerava o contrato
            // do banco e o que a Dani digitou sumia (relatado 14/09). Vai o corpo como está na tela.
            const fragmento = (corpoRef.current && corpoRef.current.innerHTML) || htmlSalvo || "";
            const editado = htmlSalvo && fragmento
                ? montarHtmlParaPdf(fragmento, `Contrato nº ${numero} — ${empresa.razaoSocial || ""}`)
                : undefined;
            const r = await fetch(`http://127.0.0.1:3456/contrato/${emp.id}${soParaMim ? "?aqui=1" : ""}`, {
                method: "POST",
                headers: { "X-Help": "1", "Content-Type": "application/json" },
                body: JSON.stringify(editado ? { editado } : {}),
                signal: controle.signal,
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok || !j.ok) throw new Error(j.mensagem || `robô respondeu ${r.status}`);
            setEnvio({ estado: "ok", msg: j.mensagem || "Enviado pelo robô." });
        } catch (e) {
            // robô não está neste PC (ou está fora do ar): cai no WhatsApp com o comando pronto
            if (e.name === "AbortError" || /Failed to fetch|NetworkError|Load failed/i.test(String(e.message))) abrirWhatsAppComComando();
            else setEnvio({ estado: "erro", msg: `Não deu: ${e.message}` });
        } finally {
            clearTimeout(alarme);
        }
    };

    const secao = { marginBottom: "1.2em", lineHeight: 1.7, textAlign: "justify" };
    const titulo = { color: "#111" };
    const cabecalho = (sub) => (
        <div style={{textAlign: "center", marginBottom: "1.5em"}}>
            <h3 style={{fontWeight: 800, color: "#0d1b2a", letterSpacing: 1}}>{HELP.nome}</h3>
            <div style={{color: "#555", fontSize: "0.85em"}}>Crédito inteligente para quem faz o futuro acontecer</div>
            <h5 style={{marginTop: "1.2em", fontWeight: 700, color: "#111"}}>{sub}</h5>
        </div>
    );
    const linhaAssinatura = (nome, papel, extra) => (
        <div className="assinatura-contrato">
            <div style={{borderTop: "1px solid #111", paddingTop: 6, marginTop: 70}}>
                <strong style={titulo}>{nome}</strong>
                <div style={{fontSize: "0.8em", color: "#555"}}>{papel}</div>
                {extra && <div style={{fontSize: "0.75em", color: "#777"}}>{extra}</div>}
            </div>
        </div>
    );

    return (
        <div className="pagina" style={{maxWidth: 900}}>
            <div className="nao-imprimir" style={{display: "flex", justifyContent: "space-between", marginBottom: "1em", flexWrap: "wrap", gap: 8}}>
                <Link className="btn btn-outline-he" to={`/emprestimo/${emp.id}`}><i className="bi bi-arrow-left"></i> Voltar</Link>
                <div style={{display: "flex", gap: 8, flexWrap: "wrap"}}>
                    {editando
                        ? <button className="btn btn-he" onClick={concluirEdicao}><i className="bi bi-check2"></i> Concluir edição</button>
                        : <button className="btn btn-outline-he" onClick={iniciarEdicao}><i className="bi bi-pencil"></i> Editar texto</button>}
                    {htmlSalvo && !editando && <button className="btn btn-outline-he" onClick={restaurarOriginal}><i className="bi bi-arrow-counterclockwise"></i> Restaurar original</button>}
                    <button
                        className="btn btn-outline-he"
                        disabled={envio.estado === "enviando"}
                        onClick={() => enviarPeloRobo(true)}
                        title="O robô gera o PDF e manda só pra você conferir"
                    ><i className="bi bi-eye"></i> Ver no meu WhatsApp</button>
                    <button
                        className="btn btn-outline-he"
                        disabled={envio.estado === "enviando"}
                        onClick={() => enviarPeloRobo(false)}
                        title={`O robô gera o PDF e manda pro WhatsApp cadastrado do cliente (${empresa.telefone || "sem telefone"}), com cópia pra você`}
                    ><i className="bi bi-whatsapp"></i> {envio.estado === "enviando" ? "Enviando…" : "Enviar pelo WhatsApp"}</button>
                    <button className="btn btn-he" onClick={imprimir}><i className="bi bi-printer"></i> Imprimir / Salvar PDF</button>
                </div>
            </div>
            {envio.msg && (
                <div className="nao-imprimir" style={{marginBottom: "1em", padding: "10px 14px", borderRadius: 10, fontSize: "0.9em", fontWeight: 600,
                    background: envio.estado === "ok" ? "rgba(46,204,113,0.12)" : envio.estado === "erro" ? "rgba(231,76,60,0.12)" : "rgba(212,175,55,0.12)",
                    color: envio.estado === "ok" ? "#2ecc71" : envio.estado === "erro" ? "#ff6b5b" : "#d4af37"}}>
                    {envio.msg}
                </div>
            )}
            {faltamNoContrato.length > 0 && (
                <div className="nao-imprimir" style={{marginBottom: "1em", padding: "10px 14px", borderRadius: 10, fontSize: "0.92em", fontWeight: 600, background: "rgba(231,76,60,0.12)", color: "#ff6b5b"}}>
                    Falta preencher antes de enviar (o contrato é assinado online): {faltamNoContrato.join(" · ")}
                </div>
            )}
            <div className="nao-imprimir" style={{marginBottom: "1em", color: "#8a95a6", fontSize: "0.82em"}}>
                <strong style={{color: "#d4af37"}}>Enviar pelo WhatsApp</strong>: o robô manda o contrato pro número cadastrado ({empresa.telefone || "sem telefone no cadastro"}), com cópia pra você — um clique quando você está no PC do robô; no celular, abre o WhatsApp com <code>#contrato {emp.id}</code> pronto e você toca em enviar.
                {(emp.Documentos || []).some((d) => d.tipo === "contrato_assinado" || /contrato.*assinad/i.test(d.nome || ""))
                    ? <span style={{color: "#2ecc71"}}> Este contrato tem <strong>versão assinada</strong> no cadastro: o envio manda a assinada (pra mandar a versão atual sem assinatura, use <code>#contrato {emp.id} novo</code>).</span>
                    : <span style={{color: "#e0a800"}}> Ainda sem versão assinada no cadastro: o envio manda o PDF atual, sem assinatura.</span>}
            </div>
            {editando && <div className="nao-imprimir" style={{marginBottom: "1em", color: "#d4af37", fontSize: "0.9em"}}>Clique em qualquer texto do contrato e digite — bens do Anexo I, endereço, datas, cláusulas. Ao concluir, a versão editada fica salva neste navegador para este contrato.</div>}

            <div
                ref={corpoRef}
                contentEditable={editando}
                suppressContentEditableWarning
                spellCheck={false}
                style={editando ? {outline: "2px dashed #d4af37", outlineOffset: 8, borderRadius: 8} : undefined}
                {...(htmlSalvo ? {dangerouslySetInnerHTML: {__html: htmlSalvo}} : {})}
            >
            {htmlSalvo ? null : (<>
            <div className="he-card folha-contrato">
                {cabecalho(`INSTRUMENTO PARTICULAR DE CONTRATO DE EMPRÉSTIMO COM CONFISSÃO DE DÍVIDA E GARANTIA DE PENHOR MERCANTIL — Nº ${numero}`)}

                <div style={secao}>
                    <strong style={titulo}>REPRESENTANTE:</strong> {HELP.nome}, prestadora de serviços de crédito empresarial, inscrita no CNPJ sob nº {HELP.cnpj}, com sede em {HELP.cidade}, neste ato representada por {HELP.responsavel}, doravante denominada REPRESENTANTE.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLIENTE:</strong> {empresa.razaoSocial}{empresa.nomeFantasia ? ` (${empresa.nomeFantasia})` : ""},{docCliente ? ` inscrita no CNPJ/CPF sob nº ${docFmt(docCliente)},` : ""}
                    {enderecoEstab ? ` estabelecimento comercial com ponto físico situado em ${enderecoEstab}${empresa.cidade ? `, ${empresa.cidade}` : ""},` : ""} neste ato representada por {empresa.responsavel}
                    {qualificacaoResp ? `, ${qualificacaoResp}` : ""}, doravante denominado CLIENTE, cujo responsável responde solidariamente, com seus bens pessoais, pelas obrigações aqui assumidas.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 1ª — DO VALOR E DA QUITAÇÃO DA ENTREGA:</strong> A REPRESENTANTE entrega ao CLIENTE, a título de empréstimo, a quantia de <strong>{real(emp.valorPrincipal)}</strong>, na data de {dataBR(emp.dataContrato)},{chavePix ? ` por PIX na chave ${chavePix} (informada pelo CLIENTE),` : ""} para emprego exclusivo na atividade do estabelecimento acima identificado. O CLIENTE declara ter recebido integralmente a quantia, dando plena e geral quitação quanto à entrega.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 2ª — DA REMUNERAÇÃO E DA CONFISSÃO DE DÍVIDA:</strong> {emp.metodoJuros === "fixo"
                        ? <>Sobre o valor emprestado incide encargo fixo de <strong>{Number(emp.taxaJuros)}% sobre o total do contrato</strong>. </>
                        : <>Sobre o valor emprestado incidem juros de <strong>{Number(emp.taxaJuros)}% ao mês</strong> ({emp.metodoJuros === "price" ? "Tabela Price" : "juros simples"}). </>}
                    O CLIENTE <strong>reconhece e confessa dever</strong> à REPRESENTANTE a importância líquida, certa e exigível de <strong>{real(emp.valorTotal)}</strong> ({porExtenso(emp.valorTotal)}), valor previamente informado, calculado e aceito, sem qualquer outro acréscimo enquanto estiver em dia com os pagamentos.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 3ª — DO PAGAMENTO:</strong> A restituição será feita em <strong>{emp.numParcelas} {diarias ? "diárias" : "parcelas mensais"}</strong> de {real(emp.valorParcela)}, com primeiro vencimento em {dataBR(emp.dataPrimeiroVencimento)} e último em {dataBR(ultimoVencimento)}, conforme cronograma anexo que integra este instrumento.{diarias ? <> As diárias são pagas <strong>de segunda-feira a sábado, até as {HORA_FECHAMENTO}</strong>; <strong>aos domingos não há cobrança</strong>, e nenhuma diária vence em domingo, conforme o cronograma anexo.</> : ""} O pagamento será feito por PIX ou em espécie contra recibo; a REPRESENTANTE registra cada {parcelaNome} paga no sistema e envia o comprovante ao CLIENTE.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 4ª — DO ATRASO:</strong> O não pagamento de qualquer {parcelaNome} na data acordada sujeita o CLIENTE a multa de 2% (dois por cento) sobre o valor em atraso, juros de mora de 1% (um por cento) ao mês, pro rata die, e correção monetária pelo INPC/IBGE, sem prejuízo das {diarias ? "diárias" : "parcelas"} vincendas.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 5ª — DO VENCIMENTO ANTECIPADO:</strong> Considera-se <strong>antecipadamente vencida toda a dívida</strong>, tornando-se imediatamente exigível o saldo integral, independentemente de aviso ou notificação, se ocorrer: (a) atraso de 3 (três) {diarias ? "diárias" : "parcelas"} consecutivas ou 5 (cinco) alternadas; (b) encerramento, mudança de endereço ou transferência do estabelecimento sem comunicação prévia por escrito; (c) venda, cessão, remoção ou oneração de qualquer bem dado em garantia; (d) prestação de informação falsa à REPRESENTANTE; (e) pedido de recuperação, falência ou insolvência do CLIENTE.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 6ª — DA GARANTIA (PENHOR MERCANTIL):</strong> Em garantia do integral cumprimento deste contrato, o CLIENTE dá em <strong>penhor mercantil</strong> (arts. 1.447 a 1.450 do Código Civil) os bens móveis de sua propriedade, livres e desembaraçados de qualquer ônus, descritos e avaliados no <strong>Anexo I</strong>, cujo valor de avaliação é igual ou superior ao valor total da dívida confessada ({real(emp.valorTotal)}). Os bens empenhados permanecem na posse do CLIENTE, que os recebe na qualidade de <strong>depositário fiel</strong> (art. 1.431, parágrafo único, do Código Civil), obrigando-se a guardá-los e conservá-los em perfeito estado no endereço do estabelecimento, respondendo por sua perda, deterioração ou desvio, sendo-lhe vedado vender, ceder, emprestar, remover ou onerar tais bens sem autorização prévia e escrita da REPRESENTANTE. Se os bens se perderem, deteriorarem ou desvalorizarem, o CLIENTE reforçará a garantia em até 5 (cinco) dias, sob pena de vencimento antecipado. A REPRESENTANTE poderá levar este instrumento a registro no Cartório de Títulos e Documentos da comarca (art. 1.448 do Código Civil), correndo as despesas por conta do CLIENTE.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 7ª — DA RETIRADA E VENDA AMIGÁVEL DOS BENS:</strong> Vencida e não paga a dívida, no todo ou em parte, o CLIENTE <strong>autoriza desde já, de forma expressa, irrevogável e por escrito</strong>, que a REPRESENTANTE retire do estabelecimento os bens empenhados — e, na falta ou insuficiência destes, outros bens móveis do estabelecimento de valor equivalente ao saldo devedor, escolhidos de comum acordo — e promova sua <strong>venda amigável</strong> (art. 1.433, IV, do Código Civil) pelo melhor preço obtido, aplicando o produto na quitação do saldo devedor, encargos e despesas, e restituindo ao CLIENTE eventual sobra, sem prejuízo da cobrança de eventual saldo remanescente por qualquer outro meio. A retirada será sempre feita de forma consensual, em horário comercial, na presença do CLIENTE ou de seu preposto, mediante assinatura de <strong>termo de entrega</strong>, que vale como recibo e como prova do consentimento. As partes reconhecem que a retirada assim realizada é exercício regular de direito contratual, e não esbulho, constrangimento ou ameaça. Recusando-se o CLIENTE à entrega, a REPRESENTANTE promoverá a execução deste título e a busca e apreensão judicial dos bens, com as despesas da Cláusula 10ª.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 8ª — DA NOTA PROMISSÓRIA E DA EXECUÇÃO:</strong> Em reforço deste contrato, o CLIENTE emite neste ato, <strong>pro solvendo</strong> e vinculada a este instrumento, a <strong>nota promissória do Anexo II</strong>, no valor de {real(emp.valorTotal)}, com vencimento em {dataBR(ultimoVencimento)}, que constitui <strong>título executivo extrajudicial</strong> (art. 784, I, do Código de Processo Civil), ficando ajustado que cada {parcelaNome} paga abate proporcionalmente o valor do título e que a quitação integral do contrato implica a sua devolução ao CLIENTE. Este instrumento vale, ainda, como confissão de dívida líquida, certa e exigível.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 9ª — DA VISTORIA, DAS VISITAS E DOS REGISTROS:</strong> O CLIENTE autoriza a REPRESENTANTE a realizar visita de verificação no estabelecimento{enderecoEstab ? ` situado em ${enderecoEstab}` : ""}{emp.visitaData ? `, agendada para ${dataBR(emp.visitaData)} às ${emp.visitaHora}` : ""}, ficando a liberação do crédito condicionada ao resultado da vistoria. Autoriza ainda, durante toda a vigência deste contrato: (a) visitas de acompanhamento e de cobrança no estabelecimento, em horário comercial; (b) o contato pelo telefone e WhatsApp informados para lembretes, cobranças e avisos; (c) o <strong>registro fotográfico</strong> dos bens dados em garantia, dos comprovantes e das entregas, que integram este contrato como prova. A cobrança será feita de forma respeitosa e reservada, e os dados pessoais serão tratados exclusivamente para a execução deste contrato (Lei nº 13.709/2018).
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 10ª — DAS DESPESAS DE COBRANÇA:</strong> Em caso de cobrança extrajudicial ou judicial, o CLIENTE arcará com todas as despesas, custas e honorários advocatícios, estes fixados em 20% (vinte por cento) sobre o valor cobrado, além dos encargos da Cláusula 4ª.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 11ª — DAS DECLARAÇÕES DO CLIENTE:</strong> O CLIENTE declara que: (a) o estabelecimento possui ponto físico em funcionamento no endereço informado; (b) as informações e documentos apresentados são verdadeiros; (c) é legítimo proprietário dos bens dados em garantia; (d) leu, compreendeu e aceitou todas as cláusulas deste contrato, inclusive a autorização de retirada e venda amigável dos bens em caso de inadimplência.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 12ª — DA ASSINATURA ELETRÔNICA E DA IRREVOGABILIDADE:</strong> Este contrato é celebrado em caráter irrevogável e irretratável, obrigando as partes e seus sucessores. A tolerância quanto ao atraso não importa novação nem renúncia. As partes reconhecem a validade da manifestação de vontade por meio eletrônico (art. 10, § 2º, da MP nº 2.200-2/2001), inclusive do aceite registrado com data, hora e número de telefone do signatário no sistema da REPRESENTANTE.
                </div>

                <div style={secao}>
                    <strong style={titulo}>CLÁUSULA 13ª — DO FORO:</strong> Fica eleito o foro da comarca de Itajaí/SC para dirimir quaisquer dúvidas oriundas deste contrato, com renúncia a qualquer outro, por mais privilegiado que seja.
                </div>

                {/* continua tabela de verdade no celular: é peça de contrato e precisa
                    imprimir igual. O table-layout fixo garante que ela nunca estoure. */}
                <table className="tabela-contrato">
                    <thead>
                        <tr style={{background: "#0d1b2a", color: "#d4af37"}}>
                            <th>{diarias ? "Diária" : "Parcela"}</th>
                            <th>Vencimento</th>
                            <th>Valor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {parcelas.map((p) => (
                            <tr key={p.id}>
                                <td>{p.numero}/{emp.numParcelas}</td>
                                <td>{dataBR(p.dataVencimento)}</td>
                                <td>{real(p.valor)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {emp.obs && <div style={secao}><strong style={titulo}>OBSERVAÇÕES:</strong> {emp.obs}</div>}

                <div style={{...secao, marginTop: "2em"}}>
                    E por estarem justas e contratadas, as partes assinam o presente instrumento em 2 (duas) vias de igual teor.
                </div>
                <div style={{textAlign: "right", marginBottom: "0.5em"}}>{HELP.cidade}, {dataBR(emp.dataContrato)}.</div>

                <div className="assinaturas-contrato">
                    {linhaAssinatura(HELP.nome, `Representante — ${HELP.responsavel}`, `CNPJ ${HELP.cnpj}`)}
                    <div className="assinatura-contrato">
                        {emp.assinaturaBase64 ?
                            <div>
                                <img src={emp.assinaturaBase64} alt="Assinatura" style={{maxHeight: 70, marginBottom: -10}}/>
                                <div style={{borderTop: "1px solid #111", paddingTop: 6}}>
                                    <strong style={titulo}>{emp.nomeSignatario}</strong>
                                    <div style={{fontSize: "0.8em", color: "#555"}}>{empresa.razaoSocial} — Cliente e depositário fiel</div>
                                    <div style={{fontSize: "0.7em", color: "#888"}}>Assinado digitalmente em {emp.assinadoEm ? new Date(emp.assinadoEm).toLocaleString("pt-BR") : ""}</div>
                                </div>
                            </div>
                            :
                            <div style={{borderTop: "1px solid #111", paddingTop: 6, marginTop: 70}}>
                                <strong style={titulo}>{empresa.responsavel}</strong>
                                <div style={{fontSize: "0.8em", color: "#555"}}>{empresa.razaoSocial} — Cliente e depositário fiel</div>
                                {empresa.cpfResponsavel && <div style={{fontSize: "0.75em", color: "#777"}}>CPF {empresa.cpfResponsavel}</div>}
                            </div>
                        }
                    </div>
                </div>
            </div>

            {/* ANEXO I — bens em garantia (preenchido na vistoria, com foto de cada bem) */}
            <div className="he-card folha-contrato" style={{pageBreakBefore: "always", marginTop: "1.5em"}}>
                {cabecalho(`ANEXO I — RELAÇÃO DE BENS DADOS EM PENHOR MERCANTIL — CONTRATO Nº ${numero}`)}
                <div style={secao}>
                    Bens móveis de propriedade do CLIENTE <strong>{empresa.razaoSocial}</strong>, existentes no estabelecimento{enderecoEstab ? ` situado em ${enderecoEstab}` : ""}, dados em garantia nos termos da Cláusula 6ª. O valor total de avaliação deve ser <strong>igual ou superior a {real(emp.valorTotal)}</strong>. Anexar foto de cada bem.
                </div>
                <table className="tabela-contrato">
                    <thead>
                        <tr style={{background: "#0d1b2a", color: "#d4af37"}}>
                            <th>Bem (descrição)</th>
                            <th>Marca / modelo / nº de série</th>
                            <th>Estado</th>
                            <th>Avaliação (R$)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bensPenhor.map((b, i) => (
                            <tr key={`b${i}`}><td>{i + 1}. {b.descricao}</td><td>{celulaDoBem(b.detalhes)}</td><td>{celulaDoBem(b.estado)}</td><td>{celulaDoBem(b.avaliacao)}</td></tr>
                        ))}
                        {Array.from({length: linhasVaziasAnexo}, (_, i) => (
                            <tr key={`v${i}`} style={{height: 34}}><td>{bensPenhor.length + i + 1}.</td><td></td><td></td><td></td></tr>
                        ))}
                        <tr style={{fontWeight: 700, background: "#f4f1e6"}}><td colSpan={3}>TOTAL DA AVALIAÇÃO</td><td>{totalAvaliacao}</td></tr>
                    </tbody>
                </table>
                <div style={secao}>
                    O CLIENTE declara ser legítimo proprietário dos bens acima, que se encontram livres de qualquer ônus, e os recebe em depósito na forma da Cláusula 6ª, autorizando o registro fotográfico de cada um.
                </div>
                <div className="assinaturas-contrato">
                    {linhaAssinatura(empresa.responsavel || "", `${empresa.razaoSocial} — Cliente e depositário fiel`)}
                    {linhaAssinatura(HELP.nome, `Representante — ${HELP.responsavel}`)}
                </div>
            </div>

            {/* ANEXO II — nota promissória (guardar separada do contrato) */}
            <div className="he-card folha-contrato" style={{pageBreakBefore: "always", marginTop: "1.5em"}}>
                {cabecalho(`ANEXO II — NOTA PROMISSÓRIA — CONTRATO Nº ${numero}`)}
                <div style={{border: "2px solid #0d1b2a", padding: "18px 22px", marginTop: 10}}>
                    <div style={{display: "flex", justifyContent: "space-between", fontWeight: 700, marginBottom: 10, flexWrap: "wrap", gap: 8}}>
                        <span>NOTA PROMISSÓRIA Nº {numero}</span>
                        <span>Vencimento: {dataBR(ultimoVencimento)}</span>
                        <span style={{fontSize: "1.25em", color: "#0d1b2a"}}>{real(emp.valorTotal)}</span>
                    </div>
                    <div style={secao}>
                        No dia {dataBR(ultimoVencimento)}, pagarei por esta única via de NOTA PROMISSÓRIA a <strong>{HELP.nome}</strong>, CNPJ {HELP.cnpj}, ou à sua ordem, a quantia de <strong>{porExtenso(emp.valorTotal)}</strong>, em moeda corrente deste país, pagável na praça de {HELP.cidade}. Título emitido pro solvendo, vinculado ao contrato nº {numero}; cada {parcelaNome} paga abate o valor proporcional.
                    </div>
                    <div style={secao}>
                        <strong>Emitente:</strong> {empresa.responsavel}{empresa.cpfResponsavel ? `, CPF ${docFmt(empresa.cpfResponsavel)}` : ""} — {empresa.razaoSocial}{empresa.cnpj ? `, CNPJ/CPF ${docFmt(empresa.cnpj)}` : ""}{enderecoEstab ? `, ${enderecoEstab}` : ""}.
                    </div>
                    <div style={{textAlign: "right"}}>{HELP.cidade}, {dataBR(emp.dataContrato)}.</div>
                    <div className="assinaturas-contrato" style={{gridTemplateColumns: "1fr", maxWidth: 380}}>
                        {linhaAssinatura(empresa.responsavel || "", "Emitente")}
                    </div>
                </div>
            </div>

            </>)}
            </div>
        </div>
    )
}
